import React, { useState, useMemo, useRef, useCallback, forwardRef, useImperativeHandle, useEffect } from 'react';
import {
  AlertTriangle, ChevronDown, ChevronRight, BookOpen, ListChecks,
  FileText, Check, X, Info,
} from 'lucide-react';
import { sanitizeEnumValue } from '../../utils/displayUtils';
import { MarkdownWithCitations } from '../citations/MarkdownWithCitations';
import { RedactionText } from '../redaction/RedactionText';
import { InlineCitedText } from '../citations/InlineCitedText';
import { EntryActions } from '../ask/EntryActions';
import { useKeyFindings } from '../../contexts/KeyFindingsContext';
import type { ContextItem } from '../../contexts/AskSidebarContext';
import { useHighlightParam } from '../../hooks/useHighlightParam';

import { fromObjectiveSource, resolveDocumentId } from '../../types/citation';
import type { UnifiedCitation, ObjectiveSource } from '../../types/citation';

// --- Helpers ---

function sev(s: string | undefined): string { return sanitizeEnumValue(s) || 'info'; }

/** Strip inline citation markers like [1], [2,3] from display text */
function stripCitations(text: string): string {
  return text.replace(/\s*\[\d+(?:,\s*\d+)*\]/g, '');
}

// --- Finding types ---

interface UnifiedFinding {
  id: string;
  source: 'soc' | 'comp';
  type: 'breach' | 'deviation' | 'concern' | 'questionable' | 'missed' | 'compliant';
  title: string;
  timestamp?: string;
  priority: number; // lower = higher priority
  data: any; // original item
}

// --- Priority scores ---

const FINDING_PRIORITY: Record<string, number> = {
  breach: 0,
  questionable: 1,
  concern: 2,
  missed: 3,
  deviation: 4,
  compliant: 5,
};

// --- Finding extraction ---

function extractFindings(socContent: any | null, compContent: any | null): UnifiedFinding[] {
  const findings: UnifiedFinding[] = [];
  let idx = 0;

  if (socContent) {
    for (const d of (socContent.clinicalDecisions || [])) {
      const assessment = sev(d.assessment);
      findings.push({
        id: `soc-decision-${idx++}`,
        source: 'soc',
        type: assessment === 'breach' ? 'breach' : assessment === 'deviation' ? 'deviation' : 'compliant',
        title: stripCitations(d.description || ''),
        timestamp: d.timestamp,
        priority: FINDING_PRIORITY[assessment] ?? 5,
        data: d,
      });
    }
    for (const c of (socContent.areasOfConcern || [])) {
      findings.push({
        id: `soc-concern-${idx++}`,
        source: 'soc',
        type: 'concern',
        title: stripCitations(c.concern || ''),
        priority: FINDING_PRIORITY.concern,
        data: c,
      });
    }
  }

  if (compContent) {
    for (const dp of (compContent.decisionPoints || [])) {
      const isQuestionable = dp.analysis?.wasChosenApproachReasonable === false;
      findings.push({
        id: `comp-decision-${idx++}`,
        source: 'comp',
        type: isQuestionable ? 'questionable' : 'compliant',
        title: stripCitations(dp.description || ''),
        timestamp: dp.timestamp,
        priority: isQuestionable ? FINDING_PRIORITY.questionable : FINDING_PRIORITY.compliant,
        data: dp,
      });
    }
    for (const m of (compContent.missedOpportunities || [])) {
      findings.push({
        id: `comp-missed-${idx++}`,
        source: 'comp',
        type: 'missed',
        title: stripCitations(m.opportunity || ''),
        timestamp: m.timing,
        priority: FINDING_PRIORITY.missed,
        data: m,
      });
    }
  }

  return findings.sort((a, b) => a.priority - b.priority);
}

// --- Tier assignment (per finding, not per theme) ---

type SectionTier = 'priority' | 'further' | 'context';

function getFindingTier(f: UnifiedFinding): SectionTier {
  if (f.type === 'breach' || f.type === 'questionable' || f.type === 'concern') return 'priority';
  if (f.type === 'deviation' || f.type === 'missed') return 'further';
  return 'context';
}

const SECTION_CONFIG = {
  priority: {
    label: 'Priority Review Areas',
    description: 'Areas flagged as most likely to warrant further attention',
    borderColor: 'border-red-200 dark:border-red-800',
    headerBg: 'bg-red-50/50 dark:bg-red-900/10',
    icon: <AlertTriangle className="w-4 h-4 text-red-600 dark:text-red-400" />,
  },
  further: {
    label: 'Further Consideration',
    description: 'Areas that may warrant review but are lower priority',
    borderColor: 'border-amber-200 dark:border-amber-800',
    headerBg: 'bg-amber-50/50 dark:bg-amber-900/10',
    icon: <Info className="w-4 h-4 text-amber-600 dark:text-amber-400" />,
  },
  context: {
    label: 'Reviewed & Noted',
    description: 'Decisions and practices reviewed with no significant concerns identified',
    borderColor: 'border-gray-200 dark:border-medical-700',
    headerBg: 'bg-gray-50/50 dark:bg-medical-800/50',
    icon: <Check className="w-4 h-4 text-green-600 dark:text-green-400" />,
  },
} as const;

/** Check if all findings in a section share the same type — if so, suppress per-finding labels */
function allSameType(findings: UnifiedFinding[]): boolean {
  if (findings.length <= 1) return true;
  return findings.every(f => f.type === findings[0].type);
}

// --- Citation resolution ---

function buildCitations(content: any, documents: any[]): UnifiedCitation[] {
  if (!content?.sources || !Array.isArray(content.sources)) return [];
  return content.sources
    .filter((s: any) => s && typeof s === 'object' && s.documentName)
    .map((s: any) => {
      const documentId = s.documentId || resolveDocumentId(s.documentName, documents);
      return fromObjectiveSource({ ...s, documentId } as ObjectiveSource);
    });
}

// --- Component ---

export interface UnifiedClinicalFindingsHandle {
  scrollToSection: (tier: SectionTier) => void;
}

interface UnifiedClinicalFindingsProps {
  socContent: any | null;
  compContent: any | null;
  documents: any[];
}

export const UnifiedClinicalFindings = forwardRef<UnifiedClinicalFindingsHandle, UnifiedClinicalFindingsProps>(({
  socContent, compContent, documents,
}, ref) => {
  const [expandedFindings, setExpandedFindings] = useState<Set<string>>(new Set());
  const [expandedAlternatives, setExpandedAlternatives] = useState<Set<string>>(new Set());
  const [collapsedSections, setCollapsedSections] = useState<Set<SectionTier>>(new Set(['priority', 'further', 'context']));
  const [standardsCollapsed, setStandardsCollapsed] = useState(false);
  const [notesCollapsed, setNotesCollapsed] = useState(false);
  const sectionRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  const socCitations = useMemo(() => buildCitations(socContent, documents), [socContent, documents]);
  const compCitations = useMemo(() => buildCitations(compContent, documents), [compContent, documents]);

  const findings = useMemo(() => extractFindings(socContent, compContent), [socContent, compContent]);

  const tieredFindings = useMemo(() => {
    const tiers: Record<SectionTier, UnifiedFinding[]> = { priority: [], further: [], context: [] };
    for (const f of findings) {
      tiers[getFindingTier(f)].push(f);
    }
    return tiers;
  }, [findings]);

  const toggleFinding = useCallback((id: string) => {
    setExpandedFindings(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }, []);
  const toggleAlt = useCallback((id: string) => {
    setExpandedAlternatives(prev => { const n = new Set(prev); if (n.has(id)) n.delete(id); else n.add(id); return n; });
  }, []);
  const toggleSection = useCallback((tier: SectionTier) => {
    setCollapsedSections(prev => { const n = new Set(prev); if (n.has(tier)) n.delete(tier); else n.add(tier); return n; });
  }, []);

  const getCitations = useCallback((source: 'soc' | 'comp') => source === 'soc' ? socCitations : compCitations, [socCitations, compCitations]);

  const scrollToSection = useCallback((tier: SectionTier) => {
    const el = sectionRefs.current.get(tier);
    if (el) {
      setCollapsedSections(prev => { const n = new Set(prev); n.delete(tier); return n; });
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, []);

  useImperativeHandle(ref, () => ({ scrollToSection }), [scrollToSection]);

  // Auto-expand and scroll to a highlighted finding from URL param
  const { highlightId, scrollToHighlighted } = useHighlightParam();
  useEffect(() => {
    if (!highlightId || findings.length === 0) return;
    // Find which tier contains this finding
    for (const [tier, items] of Object.entries(tieredFindings) as [SectionTier, UnifiedFinding[]][]) {
      if (items.some(f => f.id === highlightId)) {
        setCollapsedSections(prev => { const n = new Set(prev); n.delete(tier); return n; });
        setExpandedFindings(prev => { const n = new Set(prev); n.add(highlightId); return n; });
        // Wait for React to re-render with expanded section, then scroll
        requestAnimationFrame(() => scrollToHighlighted());
        return;
      }
    }
  }, [highlightId, findings, tieredFindings, scrollToHighlighted]);

  // Standards referenced (from SoC)
  const applicableStandards = socContent?.applicableStandards || [];
  // Merged limitations (deduplication handled by LLM prompts)
  const limitations = [
    ...(socContent?.limitations || []),
    ...(compContent?.limitations || []),
  ];
  // Merged metadata
  const socMeta = socContent?.metadata;
  const compMeta = compContent?.metadata;

  return (
    <div className="space-y-4">
      {/* Tiered sections — flat finding list, no theme grouping */}
      {(['priority', 'further', 'context'] as SectionTier[]).map(tier => {
        const sectionFindings = tieredFindings[tier];
        if (sectionFindings.length === 0 && tier !== 'priority') return null;

        const config = SECTION_CONFIG[tier];
        const isCollapsed = collapsedSections.has(tier);

        return (
          <div
            key={tier}
            ref={el => { if (el) sectionRefs.current.set(tier, el); }}
            className={`scroll-mt-4 border rounded-lg overflow-hidden ${config.borderColor}`}
          >
            {/* Section header */}
            <button
              onClick={() => toggleSection(tier)}
              className={`w-full flex items-center justify-between p-3.5 transition-colors ${config.headerBg}`}
            >
              <div className="flex items-center gap-2.5">
                {config.icon}
                <div className="text-left">
                  <h4 className="text-sm font-semibold text-gray-900 dark:text-medical-100">{config.label}</h4>
                  <p className="text-xs text-gray-500 dark:text-medical-400">
                    {sectionFindings.length === 0
                      ? 'No areas flagged for priority review'
                      : `${sectionFindings.length} ${sectionFindings.length === 1 ? 'finding' : 'findings'} — ${config.description.toLowerCase()}`}
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2">
                <span className="text-xs font-medium text-gray-500 dark:text-medical-400">{sectionFindings.length}</span>
                {isCollapsed ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
              </div>
            </button>

            {/* LLM tier summary — always visible, never collapses */}
            {sectionFindings.length > 0 && (() => {
              const socTier = socContent?.tierSummaries?.[tier];
              const compTier = compContent?.tierSummaries?.[tier];
              const tierText = socTier || compTier;
              const fallbackText = tier === 'priority' ? (socContent?.summary || compContent?.summary) : null;
              const summaryText = tierText || fallbackText;
              if (!summaryText) return null;
              return (
                <div className={`px-4 py-3 ${!isCollapsed ? 'border-b border-gray-100 dark:border-medical-700' : ''}`}>
                  <p className="text-sm text-gray-600 dark:text-medical-400 leading-relaxed">
                    <MarkdownWithCitations
                      content={stripCitations(summaryText)}
                      citations={[...socCitations, ...compCitations]}
                    />
                  </p>
                </div>
              );
            })()}

            {/* Collapsed hint */}
            {isCollapsed && sectionFindings.length > 0 && (
              <button
                onClick={() => toggleSection(tier)}
                className="w-full px-4 py-2.5 text-left hover:bg-gray-50/50 dark:hover:bg-medical-700/10 transition-colors flex items-center gap-2"
              >
                <ChevronRight className="w-4 h-4 flex-shrink-0 text-gray-400 dark:text-medical-500" />
                <span className="text-sm text-cyan-600 dark:text-cyan-400 font-medium">
                  Show {sectionFindings.length} {sectionFindings.length === 1 ? 'finding' : 'findings'}
                </span>
              </button>
            )}

            {/* Expanded findings list */}
            {!isCollapsed && (
              <div className="space-y-0.5">
                {sectionFindings.length === 0 && tier === 'priority' && (
                  <div className="text-center py-6 text-sm text-gray-500 dark:text-medical-400">
                    <Check className="w-8 h-8 text-green-500 mx-auto mb-2" />
                    No areas flagged for priority review.
                  </div>
                )}
                {sectionFindings.map(finding => (
                  <FindingCard
                    key={finding.id}
                    finding={finding}
                    isExpanded={expandedFindings.has(finding.id)}
                    expandedAlternatives={expandedAlternatives}
                    onToggle={() => toggleFinding(finding.id)}
                    onToggleAlt={toggleAlt}
                    citations={getCitations(finding.source)}
                    showLabel={!allSameType(sectionFindings)}
                  />
                ))}
              </div>
            )}
          </div>
        );
      })}

      {/* Standards Referenced */}
      {applicableStandards.length > 0 && (
        <div
          ref={el => { if (el) sectionRefs.current.set('standards', el); }}
          className="scroll-mt-4 border border-gray-200 dark:border-medical-700 rounded-lg overflow-hidden"
        >
          <button
            onClick={() => setStandardsCollapsed(prev => !prev)}
            className="w-full flex items-center justify-between p-3.5 bg-gray-50/50 dark:bg-medical-800/50"
          >
            <div className="flex items-center gap-2.5">
              <BookOpen className="w-4 h-4 text-cyan-600 dark:text-cyan-400" />
              <div className="text-left">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-medical-100">Standards Referenced</h4>
                <p className="text-xs text-gray-500 dark:text-medical-400">{applicableStandards.length} clinical standards and guidelines</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <span className="text-xs font-medium text-gray-500 dark:text-medical-400">{applicableStandards.length}</span>
              {standardsCollapsed ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
            </div>
          </button>
          {!standardsCollapsed && <div className="px-4 pb-3 divide-y divide-gray-100 dark:divide-medical-700">
            {applicableStandards.map((std: any, i: number) => (
              <div key={i} className="py-2.5 text-sm">
                <p className="font-medium text-gray-900 dark:text-medical-100">{std.title}</p>
                {std.relevance && <p className="text-xs text-gray-500 dark:text-medical-400 mt-0.5">{std.relevance}</p>}
                {std.datePublished && <p className="text-xs text-gray-400 dark:text-medical-500">Published: {std.datePublished}</p>}
              </div>
            ))}
          </div>}
        </div>
      )}

      {/* Analysis Notes */}
      {(limitations.length > 0 || socMeta || compMeta) && (
        <div className="border border-gray-200 dark:border-medical-700 rounded-lg overflow-hidden">
          <button
            onClick={() => setNotesCollapsed(prev => !prev)}
            className="w-full flex items-center justify-between p-3.5 bg-gray-50/50 dark:bg-medical-800/50"
          >
            <div className="flex items-center gap-2.5">
              <ListChecks className="w-4 h-4 text-gray-500 dark:text-gray-400" />
              <div className="text-left">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-medical-100">Analysis Notes</h4>
                <p className="text-xs text-gray-500 dark:text-medical-400">
                  {[
                    socMeta?.documentsAnalysed || socMeta?.documentsAnalyzed ? `${socMeta.documentsAnalysed || socMeta.documentsAnalyzed} documents analysed` : null,
                    socMeta?.standardsReferenced ? `${socMeta.standardsReferenced} standards referenced` : null,
                    compMeta?.decisionPointsAnalysed || compMeta?.decisionPointsAnalyzed ? `${compMeta.decisionPointsAnalysed || compMeta.decisionPointsAnalyzed} decision points reviewed` : null,
                    compMeta?.alternativesConsidered ? `${compMeta.alternativesConsidered} alternatives considered` : null,
                  ].filter(Boolean).join(' · ')}
                </p>
              </div>
            </div>
            {notesCollapsed ? <ChevronRight className="w-4 h-4 text-gray-400" /> : <ChevronDown className="w-4 h-4 text-gray-400" />}
          </button>
          {!notesCollapsed && <div className="px-4 pb-3 space-y-2">
            {/* Metadata */}
            <div className="flex flex-wrap gap-3 text-xs text-gray-500 dark:text-medical-400">
              {socMeta?.documentsAnalyzed && <span>{socMeta.documentsAnalyzed} documents analysed</span>}
              {socMeta?.standardsReferenced && <span>{socMeta.standardsReferenced} standards referenced</span>}
              {compMeta?.decisionPointsAnalyzed && <span>{compMeta.decisionPointsAnalyzed} decision points reviewed</span>}
              {compMeta?.alternativesConsidered && <span>{compMeta.alternativesConsidered} alternatives considered</span>}
            </div>
            {/* Limitations */}
            {limitations.length > 0 && (
              <ul className="text-xs text-gray-500 dark:text-medical-400 space-y-1">
                {limitations.map((l: string, i: number) => (
                  <li key={i} className="flex items-start gap-1.5">
                    <span className="text-gray-400 mt-0.5">•</span>
                    <span>{typeof l === 'string' ? l : JSON.stringify(l)}</span>
                  </li>
                ))}
              </ul>
            )}
            {/* Disclaimer */}
            <p className="text-xs text-gray-400 dark:text-medical-500 italic pt-2 border-t border-gray-100 dark:border-medical-700">
              This analysis identifies areas for expert review. It does not constitute clinical or legal opinion.
            </p>
          </div>}
        </div>
      )}
    </div>
  );
});

// --- Finding card ---

interface FindingCardProps {
  finding: UnifiedFinding;
  isExpanded: boolean;
  expandedAlternatives: Set<string>;
  onToggle: () => void;
  onToggleAlt: (id: string) => void;
  citations: UnifiedCitation[];
  showLabel?: boolean;
}

const FindingCard: React.FC<FindingCardProps> = ({
  finding, isExpanded, expandedAlternatives, onToggle, onToggleAlt, citations, showLabel = true,
}) => {
  const neutralLabel = getNeutralLabel(finding.type);
  const { isEventMarked } = useKeyFindings();
  const isMarked = isEventMarked(finding.id);

  return (
    <div data-event-id={finding.id} className={`transition-all duration-150 rounded-lg border ${isMarked ? 'border-amber-300 dark:border-amber-600 shadow-sm' : 'border-transparent'} ${isExpanded ? 'bg-gray-50/50 dark:bg-medical-700/20' : ''}`}>
      <button
        onClick={onToggle}
        className="w-full flex items-start gap-2 p-3 text-left hover:bg-gray-50 dark:hover:bg-medical-700/30 transition-colors group rounded-lg"
      >
        {/* Expand chevron — always visible on left */}
        {isExpanded
          ? <ChevronDown className="w-4 h-4 text-gray-400 flex-shrink-0 mt-0.5" />
          : <ChevronRight className="w-4 h-4 text-gray-300 dark:text-medical-600 group-hover:text-gray-500 dark:group-hover:text-medical-400 flex-shrink-0 mt-0.5 transition-colors" />}
        <div className="flex-1 min-w-0">
          <span className="text-sm text-gray-900 dark:text-medical-100">
            <InlineCitedText text={finding.title} citations={citations} />
          </span>
          {showLabel && (
            <p className="text-xs text-gray-500 dark:text-medical-400 mt-0.5">{neutralLabel}</p>
          )}
        </div>
        <EntryActions
          compact
          item={{
            id: finding.id,
            type: (finding.source === 'soc' ? 'standard-of-care' : 'comparative') as ContextItem['type'],
            summary: finding.title.slice(0, 80),
            source: `Clinical Standards — ${neutralLabel}`,
            data: finding.data,
          }}
        />
      </button>

      {isExpanded && (
        <div className="px-3 pb-3 pt-0 ml-6 space-y-2 text-sm text-gray-700 dark:text-medical-300">
          {finding.source === 'soc' && renderSocDetail(finding, citations)}
          {finding.source === 'comp' && finding.type === 'missed' && renderMissedDetail(finding, citations)}
          {finding.source === 'comp' && finding.type !== 'missed' && renderCompDecisionDetail(finding, expandedAlternatives, onToggleAlt, citations)}
        </div>
      )}
    </div>
  );
};

// --- Neutral labels ---

function getNeutralLabel(type: UnifiedFinding['type']): string {
  switch (type) {
    case 'breach': return 'Possible departure from standard';
    case 'deviation': return 'Variation from expected practice';
    case 'questionable': return 'Decision flagged for review';
    case 'missed': return 'Alternative approach identified';
    case 'concern': return 'Area flagged for attention';
    case 'compliant': return 'Reviewed — no concerns noted';
    default: return '';
  }
}

// --- Detail renderers ---

function renderSocDetail(finding: UnifiedFinding, citations: UnifiedCitation[]): React.ReactNode {
  const item = finding.data;
  const Cited: React.FC<{ text?: string }> = ({ text }) =>
    text ? <MarkdownWithCitations content={text} citations={citations} /> : null;

  // Clinical decision
  if (item.assessment !== undefined) {
    return (
      <>
        {item.timestamp && <p className="text-xs text-gray-500 dark:text-medical-400">{item.timestamp}</p>}
        {item.standardRequired && (
          <div className="p-2 bg-green-50 dark:bg-green-900/20 rounded border border-green-200 dark:border-green-800">
            <p className="text-xs font-medium text-green-800 dark:text-green-300">Expected Standard</p>
            <div className="text-sm"><Cited text={item.standardRequired} /></div>
          </div>
        )}
        {item.actualCare && (
          <div className="p-2 bg-gray-50 dark:bg-medical-700/30 rounded border border-gray-200 dark:border-medical-600">
            <p className="text-xs font-medium text-gray-500 dark:text-medical-400">What Was Observed</p>
            <div className="text-sm"><Cited text={item.actualCare} /></div>
          </div>
        )}
        {item.justification && <div><span className="font-medium text-gray-900 dark:text-medical-100">Rationale: </span><Cited text={item.justification} /></div>}
        {renderEvidenceNote(item, citations)}
      </>
    );
  }

  // Area of concern
  return (
    <>
      {item.potentialImpact && <div><span className="font-medium text-gray-900 dark:text-medical-100">Potential significance: </span><Cited text={item.potentialImpact} /></div>}
      {item.recommendations && <div><span className="font-medium text-gray-900 dark:text-medical-100">Consideration: </span><Cited text={item.recommendations} /></div>}
    </>
  );
}

function renderCompDecisionDetail(
  finding: UnifiedFinding,
  expandedAlternatives: Set<string>,
  toggleAlt: (id: string) => void,
  citations: UnifiedCitation[],
): React.ReactNode {
  const item = finding.data;
  const chosen = item.chosenApproach;
  const alternatives = item.alternatives || [];
  const analysis = item.analysis;

  return (
    <>
      {item.timestamp && <p className="text-xs text-gray-500 dark:text-medical-400">{item.timestamp}</p>}
      {item.clinicalContext && <div className="text-xs text-gray-500 dark:text-medical-400"><MarkdownWithCitations content={item.clinicalContext} citations={citations} /></div>}

      {/* Chosen approach */}
      {chosen && (
        <div className="p-2.5 bg-cyan-50 dark:bg-cyan-900/20 rounded-lg border border-cyan-200 dark:border-cyan-800">
          <p className="text-xs font-medium text-cyan-800 dark:text-cyan-300 mb-1">Approach Taken</p>
          <p className="text-sm font-medium text-gray-900 dark:text-medical-100"><RedactionText text={chosen.approach} /></p>
          {chosen.rationale && <div className="text-sm mt-1"><MarkdownWithCitations content={chosen.rationale} citations={citations} /></div>}
          {renderEvidenceNote({ citations: chosen.citations }, citations)}
        </div>
      )}

      {/* Alternatives */}
      {alternatives.length > 0 && (
        <div className="space-y-1">
          <p className="text-xs font-medium text-gray-500 dark:text-medical-400">Alternative Approaches ({alternatives.length})</p>
          {alternatives.map((alt: any, altIdx: number) => {
            const altKey = `${finding.id}-alt-${altIdx}`;
            const altOpen = expandedAlternatives.has(altKey);
            return (
              <div key={altKey} className="border border-gray-200 dark:border-medical-600 rounded-lg overflow-hidden">
                <button
                  onClick={() => toggleAlt(altKey)}
                  className="w-full flex items-start gap-2 p-2.5 text-left hover:bg-gray-50 dark:hover:bg-medical-700/30 transition-colors"
                >
                  <span className="text-sm flex-1"><RedactionText text={alt.approach} /></span>
                  {altOpen ? <ChevronDown className="w-3 h-3 text-gray-400 mt-1" /> : <ChevronRight className="w-3 h-3 text-gray-400 mt-1" />}
                </button>
                {altOpen && (
                  <div className="px-3 pb-3 space-y-2 text-sm">
                    {alt.wasConsidered !== undefined && (
                      <p className="flex items-center gap-1 text-xs">
                        {alt.wasConsidered
                          ? <><Check className="w-3 h-3 text-green-600" /> Was considered</>
                          : <><X className="w-3 h-3 text-red-600" /> Not documented as considered</>}
                      </p>
                    )}
                    {alt.reasonNotChosen && <div className="text-xs text-gray-500 dark:text-medical-400"><MarkdownWithCitations content={alt.reasonNotChosen} citations={citations} /></div>}
                    {alt.comparison && (
                      <div className="grid grid-cols-2 gap-2">
                        {alt.comparison.advantages?.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-green-700 dark:text-green-400">Potential Advantages</p>
                            <ul className="text-xs list-disc list-inside">
                              {alt.comparison.advantages.map((a: string, i: number) => <li key={i}><MarkdownWithCitations content={a} citations={citations} /></li>)}
                            </ul>
                          </div>
                        )}
                        {alt.comparison.disadvantages?.length > 0 && (
                          <div>
                            <p className="text-xs font-medium text-red-700 dark:text-red-400">Potential Disadvantages</p>
                            <ul className="text-xs list-disc list-inside">
                              {alt.comparison.disadvantages.map((d: string, i: number) => <li key={i}><MarkdownWithCitations content={d} citations={citations} /></li>)}
                            </ul>
                          </div>
                        )}
                      </div>
                    )}
                    {alt.comparison?.riskBenefit && <div className="text-xs"><span className="font-medium">Risk/Benefit: </span><MarkdownWithCitations content={alt.comparison.riskBenefit} citations={citations} /></div>}
                    {alt.likelyOutcome && <div className="text-xs"><span className="font-medium">Potential outcome: </span><MarkdownWithCitations content={alt.likelyOutcome} citations={citations} /></div>}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}

      {/* Analysis summary */}
      {analysis && (
        <div className="p-2.5 bg-gray-50 dark:bg-medical-700/30 rounded-lg border border-gray-200 dark:border-medical-600 space-y-1.5">
          <p className="text-xs font-medium text-gray-500 dark:text-medical-400">Analysis Summary</p>
          {analysis.wouldAlternativeHaveBeenBetter && (
            <p className="text-xs text-gray-600 dark:text-medical-400">
              Alternative approach assessed as <span className="font-medium">{sev(analysis.wouldAlternativeHaveBeenBetter)}</span> preferable
            </p>
          )}
          {analysis.keyFactors?.length > 0 && (
            <ul className="text-xs list-disc list-inside text-gray-600 dark:text-medical-400">
              {analysis.keyFactors.map((f: string, i: number) => <li key={i}><MarkdownWithCitations content={f} citations={citations} /></li>)}
            </ul>
          )}
        </div>
      )}
    </>
  );
}

function renderMissedDetail(finding: UnifiedFinding, citations: UnifiedCitation[]): React.ReactNode {
  const item = finding.data;
  return (
    <>
      {item.timing && <p className="text-xs text-gray-500 dark:text-medical-400">Timing: {item.timing}</p>}
      {item.potentialBenefit && (
        <div>
          <span className="font-medium text-gray-900 dark:text-medical-100">Potential significance: </span>
          <MarkdownWithCitations content={item.potentialBenefit} citations={citations} />
        </div>
      )}
    </>
  );
}

/** Render an evidence note instead of a confidence badge */
function renderEvidenceNote(item: any, citations: UnifiedCitation[]): React.ReactNode {
  const itemCitations = item.citations || [];
  if (itemCitations.length > 0) {
    const refs = itemCitations
      .map((c: any) => `${c.documentName || 'Document'}${c.pageNumber ? ` (p.${c.pageNumber})` : ''}`)
      .join(', ');
    return (
      <p className="text-xs text-gray-500 dark:text-medical-400 flex items-start gap-1 mt-1">
        <FileText className="w-3 h-3 mt-0.5 flex-shrink-0" />
        Referenced in: {refs}
      </p>
    );
  }
  return null;
}
